import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ChevronLeft, Upload, Sparkles, Check, AlertCircle, Loader, Trash2, Plus, X, Camera } from 'lucide-react';
import { extractRecipesFromImage } from '../utils/claudeAPI';
import { saveRecipes } from '../utils/recipeService';
import { storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import BottomNav from '../components/BottomNav';

export default function ImageUpload() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractedRecipes, setExtractedRecipes] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedName, setEditedName] = useState('');
  const [editingIngredients, setEditingIngredients] = useState(false);
  const [editedIngredients, setEditedIngredients] = useState([]);
  
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  function handleFileSelect(e) {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be less than 10MB');
      return;
    }

    setSelectedFile(file);
    setError('');
    setExtractedRecipes(null);

    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  }

  async function handleExtract() {
    if (!selectedFile) return;

    setExtracting(true);
    setError('');

    try {
      const recipes = await extractRecipesFromImage(selectedFile);
      
      if (!recipes || recipes.length === 0) {
        setError('No recipes found in the image. Try a clearer screenshot or a different image.');
        setExtractedRecipes(null);
      } else {
        setExtractedRecipes(recipes);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to extract recipes. Please try again or use a different image.');
    } finally {
      setExtracting(false);
    }
  }

  async function handleSave() {
    if (!extractedRecipes || extractedRecipes.length === 0) return;

    setSaving(true);
    setError('');

    try {
      let imageUrl = null;
      
      if (selectedFile) {
        const timestamp = Date.now();
        const filename = `${currentUser.uid}/${timestamp}_${selectedFile.name}`;
        const storageRef = ref(storage, `recipe-images/${filename}`);
        
        await uploadBytes(storageRef, selectedFile);
        imageUrl = await getDownloadURL(storageRef);
      }
      
      // Safety check to ensure extractedRecipes is an array
      const recipesArray = Array.isArray(extractedRecipes) ? extractedRecipes : [extractedRecipes];
      
      const recipesWithSource = recipesArray.map(recipe => ({
        ...recipe,
        source: 'Your Budget Bestie',
        tags: [],
        imageUrl: imageUrl
      }));
      
      console.log('Saving recipes:', recipesWithSource);
      
      await saveRecipes(currentUser.uid, recipesWithSource);
      
      navigate('/library');
    } catch (err) {
      console.error('Error saving recipes:', err);
      setError('Failed to save recipes. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  function startEditingName(index) {
    setEditingIndex(index);
    setEditedName(extractedRecipes[index].name);
  }

  function saveEditedName() {
    if (editingIndex === null) return;
    
    const updated = [...extractedRecipes];
    updated[editingIndex].name = editedName;
    setExtractedRecipes(updated);
    setEditingIndex(null);
    setEditedName('');
  }

  function deleteRecipe(index) {
    const updated = extractedRecipes.filter((_, i) => i !== index);
    setExtractedRecipes(updated.length > 0 ? updated : null);
  }

  function startEditingIngredients(index) {
    setEditingIngredients(true);
    setEditingIndex(index);
    setEditedIngredients([...extractedRecipes[index].ingredients]);
  }

  function saveEditedIngredients() {
    if (editingIndex === null) return;
    
    const updated = [...extractedRecipes];
    updated[editingIndex].ingredients = editedIngredients;
    setExtractedRecipes(updated);
    setEditingIngredients(false);
    setEditingIndex(null);
    setEditedIngredients([]);
  }

  function updateIngredient(idx, value) {
    const updated = [...editedIngredients];
    updated[idx] = value;
    setEditedIngredients(updated);
  }

  function deleteIngredient(idx) {
    setEditedIngredients(editedIngredients.filter((_, i) => i !== idx));
  }

  function addIngredient() {
    setEditedIngredients([...editedIngredients, '']);
  }

  return (
    <div className="min-h-screen pb-24" style={{ background: 'linear-gradient(135deg, #f8f0f5 0%, #e8e4f3 100%)' }}>
      {/* Flowing Blob Header */}
      <div className="relative h-64 overflow-hidden mb-6">
        <div 
          className="absolute w-80 h-80 rounded-full opacity-90"
          style={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
            top: '-60px',
            left: '-60px',
            borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
            animation: 'morph 8s ease-in-out infinite',
          }}
        />
        
        <div 
          className="absolute w-64 h-64 rounded-full opacity-90"
          style={{
            background: 'linear-gradient(135deg, #fb7185 0%, #fda4af 100%)',
            top: '60px',
            right: '-80px',
            borderRadius: '60% 40% 30% 70% / 50% 60% 40% 50%',
            animation: 'morph 10s ease-in-out infinite reverse',
          }}
        />
        
        <div 
          className="absolute w-56 h-56 rounded-full"
          style={{
            background: 'linear-gradient(135deg, #ffe082 0%, #fff59d 100%)',
            bottom: '-40px',
            left: '90px',
            opacity: 0.85,
            borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
            animation: 'morph 12s ease-in-out infinite',
          }}
        />
        
        <div className="relative z-10 max-w-md mx-auto px-6 pt-8">
          <div className="flex items-center gap-3 mb-2">
            <button 
              onClick={() => navigate('/')} 
              className="bg-white/20 backdrop-blur-sm p-2 rounded-xl hover:bg-white/30 transition-colors"
              style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
            >
              <ChevronLeft size={24} className="text-white" />
            </button>
            <h1 
              className="text-white text-3xl logo"
              style={{ textShadow: '0 2px 20px rgba(0,0,0,0.3), 0 4px 8px rgba(0,0,0,0.2)' }}
            >
              Add Recipe
            </h1>
          </div>
          <p 
            className="text-white/95 text-sm pl-14"
            style={{ textShadow: '0 2px 10px rgba(0,0,0,0.2)' }}
          >
            Upload a recipe screenshot ✨
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-md mx-auto px-6 pt-6">
        {/* Upload Area */}
        {!preview && (
          <label className="block bg-white rounded-3xl p-12 text-center cursor-pointer hover:shadow-lg transition-all border-2 border-dashed border-purple-200 hover:border-purple-400">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Camera size={64} className="mx-auto mb-4 text-purple-400" />
            <h3 className="font-bold text-gray-800 mb-2 text-lg">Upload Recipe Screenshot</h3>
            <p className="text-sm text-gray-600">
              Click to select an image from your device
            </p>
          </label>
        )}

        {/* Preview & Extract */}
        {preview && !extractedRecipes && (
          <div className="space-y-4">
            <div className="bg-white rounded-3xl overflow-hidden shadow-md">
              <img src={preview} alt="Preview" className="w-full" />
            </div>

            {error && (
              <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 flex items-start gap-3">
                <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setSelectedFile(null);
                  setPreview(null);
                  setError('');
                }}
                className="btn-secondary flex-1"
              >
                Choose Different Image
              </button>
              
              <button
                onClick={handleExtract}
                disabled={extracting}
                className="btn-primary flex-1"
              >
                {extracting ? (
                  <>
                    <Loader size={20} className="animate-spin" />
                    Extracting...
                  </>
                ) : (
                  <>
                    <Sparkles size={20} />
                    Extract Recipes
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Extracted Recipes */}
        {extractedRecipes && extractedRecipes.length > 0 && (
          <div className="space-y-4">
            <div className="bg-purple-50 border-2 border-purple-200 rounded-2xl p-4">
              <p className="text-purple-800 font-semibold">
                ✨ Found {extractedRecipes.length} recipe{extractedRecipes.length !== 1 ? 's' : ''}!
              </p>
            </div>

            {extractedRecipes.map((recipe, index) => (
              <div key={index} className="bg-white rounded-3xl p-6 shadow-md">
                <div className="flex items-start justify-between mb-4">
                  {editingIndex === index && !editingIngredients ? (
                    <div className="flex-1 flex gap-2">
                      <input
                        type="text"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        className="flex-1 px-4 py-2 border-2 border-purple-300 rounded-xl focus:outline-none focus:border-purple-500"
                        autoFocus
                      />
                      <button
                        onClick={saveEditedName}
                        className="bg-purple-600 text-white px-4 py-2 rounded-xl hover:bg-purple-700"
                      >
                        <Check size={20} />
                      </button>
                    </div>
                  ) : (
                    <h3 
                      onClick={() => startEditingName(index)}
                      className="font-bold text-gray-800 text-lg flex-1 cursor-pointer hover:text-purple-600"
                    >
                      {recipe.name}
                    </h3>
                  )}
                  
                  <button
                    onClick={() => deleteRecipe(index)}
                    className="text-red-500 hover:bg-red-50 p-2 rounded-xl transition-colors"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>

                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-gray-700">
                      Ingredients ({recipe.ingredients.length})
                    </p>
                    <button
                      onClick={() => startEditingIngredients(index)}
                      className="text-purple-600 text-sm font-semibold hover:underline"
                    >
                      Edit
                    </button>
                  </div>
                  
                  {editingIngredients && editingIndex === index ? (
                    <div className="space-y-2">
                      {editedIngredients.map((ing, idx) => (
                        <div key={idx} className="flex gap-2">
                          <input
                            type="text"
                            value={ing}
                            onChange={(e) => updateIngredient(idx, e.target.value)}
                            className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none text-sm"
                          />
                          <button
                            onClick={() => deleteIngredient(idx)}
                            className="text-red-500 hover:bg-red-50 px-3 rounded-xl"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={addIngredient}
                          className="btn-secondary flex-1 text-sm py-2"
                        >
                          <Plus size={16} /> Add Ingredient
                        </button>
                        <button
                          onClick={saveEditedIngredients}
                          className="btn-primary flex-1 text-sm py-2"
                        >
                          <Check size={16} /> Done
                        </button>
                      </div>
                    </div>
                  ) : (
                    <ul className="text-sm text-gray-600 space-y-1">
                      {recipe.ingredients.slice(0, 3).map((ing, idx) => (
                        <li key={idx}>• {ing}</li>
                      ))}
                      {recipe.ingredients.length > 3 && (
                        <li className="text-purple-600 font-semibold">
                          + {recipe.ingredients.length - 3} more
                        </li>
                      )}
                    </ul>
                  )}
                </div>

                {recipe.instructions && recipe.instructions.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2">
                      Instructions ({recipe.instructions.length} steps)
                    </p>
                    <p className="text-sm text-gray-600">
                      {recipe.instructions[0].substring(0, 100)}
                      {recipe.instructions[0].length > 100 ? '...' : ''}
                    </p>
                  </div>
                )}
              </div>
            ))}

            {error && (
              <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 flex items-start gap-3">
                <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary w-full"
            >
              {saving ? (
                <>
                  <Loader size={20} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={20} />
                  Save {extractedRecipes.length} Recipe{extractedRecipes.length !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
